import { FormApi } from "final-form";
import { observer } from "mobx-react-lite";
import { FC } from "react";

import { Heading, MessageBar, MessageBarType } from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { useAppointmentTypeScreenContext } from "@modules/settings/screens/appointments-types/context/AppointmentTypeScreenContext.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";
import { Navigate } from "@ui-components/navigation/Navigate.tsx";
import { When } from "@ui-components/withPerm.tsx";

import { OnlineSettingsFormFields } from "./OnlineSettingsFormFields.tsx";
import { OnlineSettingsFormValidator } from "./OnlineSettingsFormValidator.ts";
import { OnlineSettingsValues } from "./OnlineSettingsValues.types.ts";

export const OnlineSettingsDialog: FC = observer(() => {
  const validator = new OnlineSettingsFormValidator();
  const { omniAppointmentType, bhbAppointmentType, closeOnlineSettingModal } =
    useAppointmentTypeScreenContext();

  const { name, duration } = omniAppointmentType ?? {};
  const { core, bhb } = useStores();

  const newPatientDurationMinutes =
    bhbAppointmentType?.newPatientDurationMinutes || duration;

  const initialValues: OnlineSettingsValues = bhbAppointmentType
    ? {
        newPatientDurationMinutes,
        isAvailableExistingPatients:
          !!bhbAppointmentType.isAvailableExistingPatients,
        isAvailableNewPatients: !!bhbAppointmentType.isAvailableNewPatients,
        name: bhbAppointmentType.onlineName || undefined,
        description: bhbAppointmentType.onlineDescription || undefined,
        additionalInformation: bhbAppointmentType.emailContent || undefined,
        sortOrder: bhbAppointmentType.sortOrder || undefined
      }
    : {
        newPatientDurationMinutes: 15
      };

  const onSubmit = async (
    values: OnlineSettingsValues,
    form: FormApi<OnlineSettingsValues, Partial<OnlineSettingsValues>>
  ) => {
    if (!omniAppointmentType) {
      throw new Error("Appointment type does not exist.");
    }

    let newPatientDuration: number | null = null;

    if (!!form.getFieldState("newPatientDurationMinutes")?.dirty) {
      if (values.newPatientDurationMinutes) {
        newPatientDuration = values.newPatientDurationMinutes;
      }
    } else if (newPatientDurationMinutes) {
      newPatientDuration = newPatientDurationMinutes;
    }

    if (newPatientDuration) {
      newPatientDuration = newPatientDuration * 60;
    }

    await bhb.updateAppointmentType({
      id: omniAppointmentType.id,
      name: omniAppointmentType.name ?? "",
      availabilities: [],
      onlineName: values.name,
      onlineDescription: values.description,
      isAvailableExistingPatients: values.isAvailableExistingPatients ?? null,
      isAvailableNewPatients: values.isAvailableNewPatients ?? null,
      newPatientDuration,
      emailContent: values.additionalInformation,
      emailContentEnabled: !!values.additionalInformation,
      sortOrder:
        values.isAvailableExistingPatients || values.isAvailableNewPatients
          ? values.sortOrder ?? null
          : null
    });
  };

  const dialogName = `${
    bhbAppointmentType ? "Edit" : "New"
  } online settings dialog`;

  return (
    <SubmissionFormDialog<OnlineSettingsValues>
      dialogName={dialogName}
      dialogProps={{
        minWidth: 560,
        onDismiss: closeOnlineSettingModal,
        dialogContentProps: {
          title: (
            <Heading variant="modal-heading">
              {`'${name}' online settings`}
            </Heading>
          ),
          showCloseButton: true
        }
      }}
      onSubmit={onSubmit}
      onSubmitSucceeded={closeOnlineSettingModal}
      initialValues={initialValues}
      validate={validator.validate}
      buttonsProps={{
        submitButtonProps: {
          text: "Save"
        },
        hideButtonsSeparator: true
      }}
      styles={{ fields: { overflowY: "hidden" } }}
      hideButtons={!core.hasPermissions(Permission.BhbWrite)}
    >
      {() => (
        <>
          <When permission={[Permission.BhbWrite]}>
            <DataFetcher
              fetch={({ bhb }) =>
                bhb.getLocation(core.locationId).catch(() => null)
              }
              fallback={null}
            >
              {location =>
                !location ? (
                  <MessageBar
                    messageBarType={MessageBarType.warning}
                    styles={{ root: { marginBottom: 12 } }}
                  >
                    Location details have not been saved. Update Location
                    information in{" "}
                    <Navigate to={routes.settings.practices.basePath.pattern}>
                      Settings &gt; Practice &amp; Locations
                    </Navigate>
                    .
                  </MessageBar>
                ) : null
              }
            </DataFetcher>
          </When>
          <OnlineSettingsFormFields defaultOnlineName={name ?? ""} />
        </>
      )}
    </SubmissionFormDialog>
  );
});
